import React from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_collateralApprovalTableSearchAndFilterBar.module.scss';
import { TPendingCollateralApprovalListingFilters } from '~types/feat/customer';
import { MetalTypeEnum, TransactionStatusEnum } from '~utils/enums';
import dayjs from 'dayjs';

const CollateralApprovalTableSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();

    const filters = useAppSelector(state => state.approval.pendingCollateralListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess } = useAppSelector(state => state.auth.userDetails);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const setFilters = ( selectedFilters: TPendingCollateralApprovalListingFilters ) => dispatch(combinedActions.approvalSetPendingCollateralListingFilters( selectedFilters ));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingCollateralListingAttempt());

    const handleCancelClick = () => {
        dispatch(combinedActions.approvalClearPendingCollateralListingFilters());
        dispatch(combinedActions.approvalGetPendingCollateralListingAttempt());
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerName || '',
                setValue: (event) => setFilters({ ...filters, customerName: event.target.value || '' }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerId ? filters.customerId.toString() : '',
                setValue: (event) => setFilters({ ...filters, customerId: Number(event.target.value) }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Approved],
                        value: TransactionStatusEnum.Approved,
                    },
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Pending],
                        value: TransactionStatusEnum.Pending,
                    },
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Rejected],
                        value: TransactionStatusEnum.Rejected,
                    },
                ],
                value: TransactionStatusEnum[filters.status as TransactionStatusEnum],
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    status: TransactionStatusEnum[event.target.value as keyof typeof TransactionStatusEnum],
                }),
                optionSelected: !!TransactionStatusEnum[filters.status as TransactionStatusEnum],
                noOptionsMessage: 'No status found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom || '',
                setDate: (date: string) => {
                    if (filters?.dateTo && date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo || '',
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: MetalTypeEnum[filters.metalType as MetalTypeEnum],
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    metalType: MetalTypeEnum[event.target.value as keyof typeof MetalTypeEnum],
                }),
                optionSelected: !!MetalTypeEnum[filters.metalType as MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default CollateralApprovalTableSearchAndFilterBar;
